import React, { Component, type ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary — catches render-time exceptions anywhere in
 * the React tree and shows a friendly recovery screen instead of a blank
 * white page. Wraps the whole App in main.tsx.
 *
 * componentDidCatch logs to console with a tag so it's findable in browser
 * devtools. No external error-reporting service wired up yet (Sentry etc.
 * would slot in here later).
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[Rankbet] Uncaught render error:", error, info.componentStack);
  }

  handleReload = () => {
    globalThis.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background:
            "linear-gradient(180deg, #0a0f1e 0%, #1a1f3e 50%, #0f1428 100%)",
          fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            width: "100%",
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.02) 100%)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            borderRadius: "16px",
            padding: "32px 28px",
            color: "white",
            textAlign: "center",
            boxShadow:
              "0 0 0 1px rgba(239, 68, 68, 0.08) inset, 0 30px 80px -20px rgba(239, 68, 68, 0.4)",
          }}
        >
          <div style={{ fontSize: "48px", marginBottom: "8px" }}>⚠️</div>
          <h1
            style={{
              fontSize: "22px",
              fontWeight: 800,
              margin: "0 0 12px 0",
              letterSpacing: "-0.02em",
            }}
          >
            Etwas ist schiefgelaufen
          </h1>
          <p
            style={{
              color: "rgba(255,255,255,0.7)",
              fontSize: "14px",
              lineHeight: 1.55,
              margin: "0 0 20px 0",
            }}
          >
            Rankbet ist auf einen unerwarteten Fehler gestoßen. Lade die
            Seite neu, deine Tipps gehen nicht verloren.
          </p>
          {this.state.error && (
            <details
              style={{
                marginBottom: "20px",
                textAlign: "left",
                fontSize: "12px",
                color: "rgba(255,255,255,0.45)",
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  outline: "none",
                  fontWeight: 600,
                  marginBottom: "6px",
                }}
              >
                Technische Details
              </summary>
              <code
                style={{
                  display: "block",
                  background: "rgba(0,0,0,0.3)",
                  padding: "8px 10px",
                  borderRadius: "6px",
                  fontFamily:
                    '"JetBrains Mono", "SF Mono", Menlo, monospace',
                  fontSize: "11px",
                  wordBreak: "break-word",
                  whiteSpace: "pre-wrap",
                }}
              >
                {this.state.error.message}
              </code>
            </details>
          )}
          <button
            type="button"
            onClick={this.handleReload}
            style={{
              padding: "14px 28px",
              background:
                "linear-gradient(135deg, #6366f1 0%, #06b6d4 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              fontSize: "15px",
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "inherit",
              letterSpacing: "-0.01em",
              boxShadow: "0 8px 24px rgba(99,102,241,0.35)",
            }}
          >
            Seite neu laden
          </button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
